import type { GuidebookProperty, GuidebookWelcome, GuidebookWeather } from "./types"
import { ChevronDownIcon } from "./icons"

interface Props {
  property: GuidebookProperty
  welcome: GuidebookWelcome | null
  weather: GuidebookWeather | null
  onExplore: () => void
  scrolled: boolean
}

export function CoverHero({ property, welcome, weather, onExplore, scrolled }: Props) {
  const initial = property.name?.charAt(0)?.toUpperCase() || "?"
  const heroMessage = (welcome?.hero_message || welcome?.message || "").trim()
  const welcomeBody = (welcome?.welcome_message || welcome?.message || "").trim()
  const showHeroMessage = heroMessage && heroMessage !== welcomeBody

  // Weather-adaptive hero gradient
  const heroStyle = getHeroStyle(weather)

  return (
    <div className="gb-hero" style={heroStyle}>
      <div className="gb-hero__monogram" aria-hidden="true">{initial}</div>
      <h1 className="gb-hero__name">{property.name}</h1>

      {property.address && (
        <p className="gb-hero__location">{property.address}</p>
      )}

      {showHeroMessage && (
        <p className="gb-hero__welcome-text">{heroMessage}</p>
      )}

      <button className="gb-hero__cta" onClick={onExplore} type="button">
        Your stay details
      </button>

      <div className={`gb-hero__scroll-hint${scrolled ? " gb-hero__scroll-hint--hidden" : ""}`} aria-hidden="true">
        <ChevronDownIcon />
      </div>
    </div>
  )
}

function getHeroStyle(weather: GuidebookWeather | null): React.CSSProperties {
  if (!weather || weather.weather_code == null) return {}

  const code = weather.weather_code
  const isDay = weather.is_day !== false

  let bg: string
  let textColor = "#ffffff"
  let textMuted = "rgba(255, 255, 255, 0.7)"

  if (code === 0 || code === 1) {
    if (isDay) {
      bg = "linear-gradient(160deg, #e8ddd0 0%, #d4c4b0 40%, #bfad96 100%)"
      textColor = "#1a1a1a"
      textMuted = "rgba(26, 26, 26, 0.6)"
    } else {
      bg = "linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)"
    }
  } else if (code === 2) {
    if (isDay) {
      bg = "linear-gradient(160deg, #ddd5c8 0%, #c9bfb0 40%, #b5aa98 100%)"
      textColor = "#1a1a1a"
      textMuted = "rgba(26, 26, 26, 0.6)"
    } else {
      bg = "linear-gradient(160deg, #1e1e30 0%, #1a2540 40%, #152e55 100%)"
    }
  } else if (code === 3) {
    bg = "linear-gradient(160deg, #d5d0ca 0%, #c5bfb7 40%, #b5afa5 100%)"
    textColor = "#1a1a1a"
    textMuted = "rgba(26, 26, 26, 0.6)"
  } else if (code === 45 || code === 48) {
    bg = "linear-gradient(160deg, #d8d5d0 0%, #ccc8c2 40%, #bfbbb5 100%)"
    textColor = "#1a1a1a"
    textMuted = "rgba(26, 26, 26, 0.6)"
  } else if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
    bg = "linear-gradient(160deg, #bfc5cc 0%, #a8b0b8 40%, #8f99a3 100%)"
    textColor = "#1a1a1a"
    textMuted = "rgba(26, 26, 26, 0.6)"
  } else if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) {
    bg = "linear-gradient(160deg, #e8e6e3 0%, #d5d3d0 40%, #c2c0bd 100%)"
    textColor = "#1a1a1a"
    textMuted = "rgba(26, 26, 26, 0.6)"
  } else if (code >= 95) {
    bg = "linear-gradient(160deg, #2d2d3d 0%, #1e2535 40%, #151b28 100%)"
  } else {
    bg = "linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)"
  }

  return {
    "--gb-bg-hero": bg,
    "--gb-hero-text-color": textColor,
    "--gb-hero-text-muted": textMuted,
  } as React.CSSProperties
}
